import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Send, Eye, Trash2, FileEdit, Calendar, Users, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';

const statusLabels = {
  draft: 'Brouillon',
  sent: 'Envoyée',
};

const statusColors = {
  draft: 'bg-amber-100 text-amber-700',
  sent: 'bg-emerald-100 text-emerald-700',
};

export default function NewsletterList() {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNewsletters();
  }, []);

  const fetchNewsletters = async () => {
    try {
      const res = await axios.get('/newsletters');
      setNewsletters(res.data.newsletters || []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const res = await axios.post('/newsletters', {
        title: 'Nouvelle Newsletter',
        articles: [],
      });
      navigate(`/newsletters/${res.data.newsletter_id}`);
    } catch (err) {
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette newsletter ?')) return;
    try {
      await axios.delete(`/newsletters/${id}`);
      setNewsletters(prev => prev.filter(n => n.newsletter_id !== id));
    } catch (err) {
    }
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="newsletter-list">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Newsletters</h1>
          <p className="text-slate-500 text-sm mt-1">Créez et envoyez des newsletters à vos contacts</p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-[#8DC63F] hover:bg-[#7ab635] text-white gap-2"
          data-testid="create-newsletter-btn"
        >
          <Plus className="h-4 w-4" />
          Nouvelle Newsletter
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Chargement...</div>
      ) : newsletters.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <Send className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">Aucune newsletter</p>
          <p className="text-slate-400 text-sm mt-1">Créez votre première newsletter !</p>
        </div>
      ) : (
        <div className="space-y-3">
          {newsletters.map(nl => (
            <div
              key={nl.newsletter_id}
              onClick={() => navigate(`/newsletters/${nl.newsletter_id}`)}
              className="bg-white rounded-xl border border-slate-200 p-5 hover:border-[#8DC63F] hover:shadow-md transition-all cursor-pointer group"
              data-testid={`newsletter-item-${nl.newsletter_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-[#0F172A] truncate">{nl.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[nl.status] || statusColors.draft}`}>
                      {statusLabels[nl.status] || nl.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <FileEdit className="h-3.5 w-3.5" />
                      {(nl.articles || []).length} article(s)
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {new Date(nl.created_at).toLocaleDateString('fr-FR')}
                    </span>
                    {nl.status === 'sent' && (
                      <span className="flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" />
                        {nl.sent_to_count} destinataire(s)
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => handleDelete(e, nl.newsletter_id)}
                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    data-testid={`delete-newsletter-${nl.newsletter_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-[#8DC63F] transition-colors" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
